import React from 'react';
import { Link } from 'react-router-dom';
import { getSourceBadge, getDomain } from '../utils/sourceLabels';

// Sprint A Phase 2 (Fix 4): replaces the small "Sources:" pill row on
// ReplyCard. Pre-Phase-2, sources rendered as text-link chips that opened the
// raw URL in a new tab — no path to challenge the evidence and no surfacing
// of `is_challenged` state. Now: each row is a chip showing the source-origin
// badge AND a Challenged badge when applicable, and the whole chip links to
// the evidence detail page where the challenge composer lives.
//
// 2026-06-10 evidence-extraction sprint: includeUrlless now defaults TRUE.
// Pre-sprint, urlless evidence_checks rows were filtered out, so even after
// the AI prompt was fixed to emit them, ReplyCard would silently drop them.
// Claimed plain-words events ("Ethiopian Airlines Flight 604 crashed in
// 1988…") are valid evidence and must render. The type icon (📍/📊/📖/💬/🔗)
// uses the new evidence_type column to label the form of the claim.
//
// Props:
//   evidence: array of evidence_checks rows.
//   postId: needed to construct the /posts/:postId/evidence/:ec.id link.
//   label: header text. Default "Incidences" (per the Phase 2 spec).
//   stopPropagation: when rendered inside a clickable parent (a ReplyCard
//          whose title navigates), stop chip clicks from bubbling.
//   includeUrlless: when true (DEFAULT post-2026-06-10), include evidence
//          rows that have no URL — claimed events/studies/anecdotes/etc.
//          Set false only on legacy surfaces that explicitly want url-only.

const TYPE_ICON = {
  incident:  '📍',
  study:     '📖',
  statistic: '📊',
  anecdote:  '💬',
  citation:  '🔗',
};

function ChallengedBadge() {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, color: '#92400e', background: '#fef3c7',
      padding: '1px 5px', borderRadius: 6, textTransform: 'uppercase',
      letterSpacing: '0.04em', fontFamily: 'var(--font-body)',
      lineHeight: 1.2,
    }}>
      ⚠ Challenged
    </span>
  );
}

export default function IncidencesList({
  evidence,
  postId,
  label = 'Incidences',
  stopPropagation = false,
  includeUrlless = true,
}) {
  const list = (evidence || []).filter(ev => includeUrlless || ev.evidence_url);
  if (list.length === 0) return null;

  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{
        fontSize: 10, color: 'var(--color-text-muted)', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        fontFamily: 'var(--font-body)', marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {list.map((ev) => {
          const badge = getSourceBadge(ev);
          const domain = ev.evidence_url ? getDomain(ev.evidence_url) : null;
          // evidence_type may be absent on legacy rows; default to 'citation'.
          const icon = TYPE_ICON[ev.evidence_type] || TYPE_ICON.citation;
          // Label preference: domain when there's a URL; otherwise the
          // claimed-event text excerpt (e.g. "Ethiopian Airlines Flight 604").
          const labelText = domain
            || (ev.evidence_text || '').slice(0, 40)
            || 'Evidence';
          const chip = (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 8px',
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              fontSize: 12, color: '#475569',
              fontFamily: 'var(--font-body)',
              flexShrink: 0,
            }}>
              <span aria-hidden="true" style={{ fontSize: 11, lineHeight: 1 }}>{icon}</span>
              <span style={{
                fontSize: 9, fontWeight: 700, color: badge.color,
                textTransform: 'uppercase', letterSpacing: '0.04em',
                lineHeight: 1.2,
              }}>
                {badge.label}
              </span>
              <span>{labelText}</span>
              {ev.is_challenged && <ChallengedBadge />}
            </span>
          );
          if (postId && ev.id) {
            return (
              <Link
                key={ev.id}
                to={`/posts/${postId}/evidence/${ev.id}`}
                onClick={stopPropagation ? e => e.stopPropagation() : undefined}
                style={{ textDecoration: 'none' }}
              >
                {chip}
              </Link>
            );
          }
          return <React.Fragment key={ev.id || `nokey-${domain || labelText}`}>{chip}</React.Fragment>;
        })}
      </div>
    </div>
  );
}
